<template>
  <main-template>
    <!-- Cabeçalho da página -->
    <v-row class="my-3">
      <v-col cols="12">
        <v-card class="pa-4">
          <h1 class="text-h4 font-weight-medium mb-2">Solicitações de Deleção</h1>
          <p class="text-subtitle-1 text-medium-emphasis">
            Analise e decida sobre as solicitações de exclusão de dados.
          </p>

          <v-alert color="info" outlined class="mt-3" icon="mdi-information" border="left" dense>
            <div class="subtitle-1">
              Ao aprovar uma solicitação, todos os registros relacionados serão excluídos
              permanentemente.
            </div>
          </v-alert>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabela de solicitações -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="solicitacoesDeDelecao"
        :search="search"
        :loading="loading"
        :items-per-page="itemsPerPage"
        mobile-breakpoint="sm"
        loading-text="Carregando solicitações..."
        hide-default-footer
        no-data-text="Nenhuma solicitação encontrada"
        class="elevation-1"
      >
        <!-- Coluna de ID -->
        <template v-slot:item.id="{ item }">
          <div class="font-weight-medium">#{{ item.id }}</div>
        </template>

        <!-- Coluna de Solicitante -->
        <template v-slot:item.creator.name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" color="grey lighten-3" class="mr-2">
              <span class="subtitle-2">
                {{ item.creator?.name ? getInitials(item.creator.name) : "?" }}
              </span>
            </v-avatar>
            <div>
              <div>{{ item.creator?.name }}</div>
              <div class="caption text-medium-emphasis">{{ item.department }}</div>
            </div>
          </div>
        </template>

        <!-- Coluna de Data -->
        <template v-slot:item.exclusable_type="{ item }">
          <div>{{ item.readable_name }}</div>
        </template>

        <!-- Coluna de Tipo de Dados -->
        <template v-slot:item.created_at="{ item }">
          <div>{{ formatDate(item.created_at) }}</div>
          <div class="caption text-medium-emphasis">{{ formatTime(item.created_at) }}</div>
        </template>

        <!-- Coluna de Registros Afetados -->
        <template v-slot:item.relatedRecords="{ item }">
          <v-chip small :color="getRecordsChipColor(item.relatedRecords)" text-color="white">
            {{ item.relatedRecords }}
          </v-chip>
        </template>

        <!-- Coluna de Status -->
        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatusColor(item.status)" text-color="white">
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Coluna de Ações -->
        <template v-slot:item.actions="{ item }">
          <div v-if="item.status === 'pending'">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="success"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="approveRequest(item)"
                >
                  <v-icon small>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Aprovar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="error" v-bind="attrs" v-on="on" @click="rejectRequest(item)">
                  <v-icon small>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Reprovar</span>
            </v-tooltip>
          </div>
          <div v-else>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="showDetails(item)">
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver detalhes</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <e-paginate
      :current_page="paginate.current_page"
      :last_page="paginate.last_page"
      :pageNumber="pageNumber"
      @changeActionResponse="loadSolicitacoesDeDelecao($event, search)"
      :search="search"
    >
    </e-paginate>

    <!-- Modal de confirmação para aprovar -->
    <v-dialog v-model="approveDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirmar aprovação</v-card-title>
        <v-card-text>
          <v-alert color="warning" outlined class="mb-3" icon="mdi-alert" border="left">
            Esta ação é irreversível.
          </v-alert>

          <p>
            Você está prestes a aprovar a solicitação <strong>#{{ selectedRequest.id }}</strong
            >.
          </p>
          <p>
            Isso resultará na exclusão permanente de
            <strong>{{ selectedRequest.relatedRecords }} registros</strong> relacionados.
          </p>
          <p>Deseja continuar?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeApproveDialog">Cancelar</v-btn>
          <v-btn color="success" @click="confirmApprove">Confirmar aprovação</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de confirmação para rejeitar -->
    <v-dialog v-model="rejectDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Reprovar solicitação</v-card-title>
        <v-card-text>
          <p>
            Você está prestes a reprovar a solicitação <strong>#{{ selectedRequest.id }}</strong
            >.
          </p>

          <v-textarea
            v-model="rejectionReason"
            label="Motivo da reprovação"
            hint="Forneça um motivo que será enviado ao solicitante"
            outlined
            rows="3"
            counter="200"
            class="mt-3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeRejectDialog">Cancelar</v-btn>
          <v-btn color="error" @click="confirmReject" :disabled="!rejectionReason"
            >Confirmar reprovação</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de detalhes da solicitação -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card class="elevation-8">
        <!-- Botão de fechar para outros status -->
        <v-col cols="12" class="d-flex justify-end">
          <v-btn color="error" small rounded @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-col>

        <!-- Cabeçalho do modal -->
        <v-card-title class="headline d-flex justify-space-between align-center pa-6 primary--text">
          <div class="d-flex flex-column">
            <span class="text-h5 font-weight-bold">Detalhes da Solicitação</span>
            <span class="text-caption text--secondary mt-1">
              {{ selectedRequest.readable_name }}
            </span>
          </div>
          <v-chip
            :color="getStatusColor(selectedRequest.status)"
            text-color="white"
            class="font-weight-bold"
            large
          >
            {{ getStatusText(selectedRequest.status) }}
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Conteúdo do modal -->
        <v-card-text class="pa-6">
          <v-container fluid class="pa-0">
            <v-row>
              <!-- Informações do solicitante -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 mb-4" elevation="1">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    <span class="text-subtitle2 font-weight-bold text--primary">Solicitante</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ selectedRequest.creator?.name }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    Perfil:
                    {{
                      selectedRequest.creator?.roles[0].name === "super-admin"
                        ? "Super Administrador"
                        : selectedRequest.creator?.roles[0].name.charAt(0).toUpperCase() +
                          selectedRequest.creator?.roles[0].name.slice(1)
                    }}
                  </div>
                </v-card>
              </v-col>

              <!-- Data da solicitação -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 mb-4" elevation="1">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
                    <span class="text-subtitle2 font-weight-bold text--primary"
                      >Data da Solicitação</span
                    >
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDate(selectedRequest.created_at) }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    {{ formatTime(selectedRequest.created_at) }}
                  </div>
                </v-card>
              </v-col>

              <!-- Tipo de exclusão -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 mb-4" elevation="1">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="warning" class="mr-2">mdi-delete-variant</v-icon>
                    <span class="text-subtitle2 font-weight-bold text--primary"
                      >Tipo de Exclusão</span
                    >
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ selectedRequest.readable_name }}
                    <span class="text--secondary text-caption">
                      (Id: {{ selectedRequest.exclusable_id }})
                    </span>
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    {{ selectedRequest.descricao }}
                  </div>
                </v-card>
              </v-col>

              <!-- Status atual -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-4 mb-4" elevation="1">
                  <div class="d-flex align-center mb-2">
                    <span class="text-subtitle2 font-weight-bold text--primary">Status Atual</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ getStatusText(selectedRequest.status) }}
                  </div>
                  <div class="text-caption text--secondary mt-1">Aguardando análise</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Seção de impactos -->
            <v-row>
              <v-col cols="12">
                <v-card outlined class="pa-4 mb-4" elevation="1">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="info" class="mr-2">mdi-chart-bar</v-icon>
                    <span class="text-subtitle2 font-weight-bold text--primary"
                      >Impactos da Exclusão</span
                    >
                  </div>

                  <v-row class="text-center">
                    <v-col
                      cols="12"
                      sm="4"
                      v-for="(item, index) in selectedRequest.impactos"
                      :key="index"
                    >
                      <div class="d-flex flex-column align-center">
                        <v-icon size="32" color="primary" class="mb-2"
                          >mdi-format-list-numbered</v-icon
                        >
                        <div class="text-h6 font-weight-bold primary--text">
                          {{ item }}
                        </div>
                        <div class="text-caption text--secondary">
                          {{
                            index
                              .split("_")
                              .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                              .filter((item) => item !== "Count")
                              .join(" ")
                          }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- Alerta de impacto -->
            <v-row v-if="selectedRequest.impactos?.etapas_count > 0">
              <v-col cols="12">
                <v-alert type="warning" outlined class="mb-4" border="left" colored-border>
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-alert-circle</v-icon>
                    <div>
                      <div class="font-weight-bold">Atenção!</div>
                      <div class="text-body-2">
                        Esta exclusão afetará {{ selectedRequest.impactos.etapas_count }} etapa(s)
                        do sistema. Verifique cuidadosamente antes de aprovar.
                      </div>
                    </div>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Ações do modal -->
        <v-card-actions class="pa-6">
          <v-row class="ma-0" no-gutters>
            <!-- Botões de ação para status pendente -->
            <v-col
              cols="12"
              v-if="selectedRequest.status === 'PENDENTE'"
              class="d-flex flex-column flex-sm-row gap-3"
            >
              <v-btn
                color="error"
                large
                outlined
                class="flex-grow-1 mb-2 mb-sm-0"
                @click="reprovarSolicitacao(selectedRequest.id)"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Reprovar Solicitação
              </v-btn>

              <v-btn
                color="success"
                large
                class="flex-grow-1"
                @click="aprovarSolicitacao(selectedRequest.id)"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Aprovar Solicitação
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loading: false,
      selectedRequest: {},
      pageNumber: 1,
      itemsPerPage: 15,
      paginate: {
        current_page: 1,
        last_page: 0,
      },
      approveDialog: false,
      rejectDialog: false,
      detailsDialog: false,
      rejectionReason: "",
      snackbar: {
        show: false,
        text: "",
        color: "",
      },
      headers: [
        { text: "ID", value: "id", sortable: true, width: "80px" },
        { text: "Solicitante", value: "creator.name", sortable: true },
        { text: "Tipo de dados", value: "exclusable_type", sortable: true },
        { text: "Data", value: "created_at", sortable: true },
        { text: "Status", value: "status", sortable: true, align: "center" },
        { text: "Ações", value: "actions", sortable: false, align: "center", width: "120px" },
      ],
      statusOptions: [
        { text: "Pendente", value: "pending" },
        { text: "Aprovado", value: "approved" },
        { text: "Reprovado", value: "rejected" },
      ],
      solicitacoesDeDelecao: [],
    };
  },
  async mounted() {
    await this.loadSolicitacoesDeDelecao(this.pageNumber, this.itemsPerPage, this.search);
  },
  methods: {
    async loadSolicitacoesDeDelecao(page, limit, search = "") {
      const response = await this.$services.solicitacoesService.syncAll({
        page,
        limit,
        search,
      });
      this.solicitacoesDeDelecao = response.data;
      this.paginate = response;
    },

    async reprovarSolicitacao(id) {
      try {
        const response = await this.$services.solicitacoesService.reprovar(id);
        this.$toast.success(response.message);
        await this.loadSolicitacoesDeDelecao(
          this.paginate.current_page,
          this.itemsPerPage,
          this.search
        );
      } catch (error) {
        this.$toast.danger(error.message);
      } finally {
        this.detailsDialog = false;
      }
    },

    async aprovarSolicitacao(id) {
      try {
        const response = await this.$services.solicitacoesService.aprovar(id);
        this.$toast.success(response.message);
        await this.loadSolicitacoesDeDelecao(
          this.paginate.current_page,
          this.itemsPerPage,
          this.search
        );
      } catch (error) {
        this.$toast.danger(error.message);
      } finally {
        this.detailsDialog = false;
      }
    },

    // Formatação de data (DD/MM/YYYY)
    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;
    },

    // Formatação de hora (HH:MM)
    formatTime(dateString) {
      const date = new Date(dateString);
      return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    },

    // Obter iniciais do nome
    getInitials(name) {
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },

    // Cor do chip de status
    getStatusColor(status) {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "error";
        case "PENDENTE":
          return "warning";
        default:
          return "grey";
      }
    },

    // Texto do status
    getStatusText(status) {
      switch (status) {
        case "approved":
          return "Aprovado";
        case "rejected":
          return "Reprovado";
        case "PENDENTE":
          return "Pendente";
        default:
          return status;
      }
    },

    // Cor do chip de registros afetados
    getRecordsChipColor(count) {
      if (count < 50) return "green";
      if (count < 500) return "orange";
      return "deep-orange";
    },

    // Mostrar diálogo de aprovação
    approveRequest(item) {
      this.selectedRequest = { ...item };
      this.approveDialog = true;
    },

    // Fechar diálogo de aprovação
    closeApproveDialog() {
      this.approveDialog = false;
    },

    // Confirmar aprovação
    confirmApprove() {
      this.loading = true;
    },

    // Mostrar diálogo de rejeição
    rejectRequest(item) {
      this.selectedRequest = { ...item };
      this.rejectionReason = "";
      this.rejectDialog = true;
    },

    // Fechar diálogo de rejeição
    closeRejectDialog() {
      this.rejectDialog = false;
    },

    // Confirmar rejeição
    confirmReject() {
      this.loading = true;

      // Simulação de processamento
      setTimeout(() => {
        // Encontrar e atualizar a solicitação
        const index = this.solicitacoesDeDelecao.findIndex(
          (req) => req.id === this.selectedRequest.id
        );
        if (index !== -1) {
          this.solicitacoesDeDelecao[index].status = "rejected";
          this.solicitacoesDeDelecao[index].rejectionReason = this.rejectionReason;
        }

        this.loading = false;
        this.rejectDialog = false;

        this.showSnackbar("Solicitação reprovada.", "error");
      }, 1000);
    },

    // Mostrar detalhes da solicitação
    showDetails(item) {
      this.selectedRequest = { ...item };
      this.detailsDialog = true;
    },

    // Exibir snackbar de notificação
  },
};
</script>

<style></style>
